import React from "react";
import {useTranslation} from "@i18n/client";
import {I18nNamespace} from "@i18n";
import {CareEntryRowVariant} from "@components/CareEntryRow";
import {getFeaturedCareSlugs} from "../../../../utils/getFeaturedCares";
import {useFetchCareEntries} from "../../../../queries/useFetchCareEntries";
import {MonolithLocationFields} from "@services/graphql/queries/location";
import {Collapse} from "@mui/material";
import {TextInput} from "@components/Input";
import NoResults from "@components/Reason/CareDiscoverySearch/NoResults";
import ResultList from "@components/Reason/CareDiscoverySearch/ResultList";
import {useCareDiscoverySearchController} from "@components/Reason/CareDiscoverySearch/useCareDiscoverySearchController";
import {useTopicStack} from "@components/Reason/CareDiscoverySearch/useTopicStack";
import {usePrefersReducedMotion} from "../../../../hooks/usePrefersReducedMotion";
import {v5Pages} from "../../../_common/_constants";
import {Row} from "@components/Booking/AppointmentReason/Row";
import Heading from "@components/Booking/Heading";
import useRelatedCares from "@components/Reason/CareDiscoverySearch/useRelatedCares";
import {RootStateApptReason} from "../../../../store/types";

type Props = {
  location?: MonolithLocationFields;
  reasons: RootStateApptReason[];
  selectedTopic: string | null;
};

const AppointmentReasonPage = ({location, reasons, selectedTopic}: Props) => {
  const featuredCareSlugs = getFeaturedCareSlugs(v5Pages.clinicDetails, location?.specialtyIds);

  const defaultCaresQuery = useFetchCareEntries(selectedTopic ? [] : featuredCareSlugs);
  const chosenCareEntryQuery = useFetchCareEntries(selectedTopic ? [selectedTopic] : []);
  const relatedCares = useRelatedCares(chosenCareEntryQuery.data?.[0]);
  const isLoading =
    defaultCaresQuery.isLoading || chosenCareEntryQuery.isLoading || relatedCares.isLoading;
  const toShow = selectedTopic ? relatedCares.data : defaultCaresQuery.data;

  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const {query, isNoSymptomsSearched, handleQueryChange, showNoResults, resultsToRender} =
    useCareDiscoverySearchController(toShow, reasons);
  const topicExpansionStack = useTopicStack();
  const shouldReduceMotion = usePrefersReducedMotion();
  const transitionSpeed = shouldReduceMotion ? 0.1 : 200;

  return (
    <div className="flex flex-col gap-6">
      <Heading>{i18n.t("What brings you in?", {ns: "web-scheduling"})}</Heading>
      <div className="flex flex-col gap-2">
        <TextInput
          onChange={handleQueryChange}
          icon="search"
          placeholder={i18n.t("Search symptoms or services", {ns: "web-scheduling"})}
          ariaLabel={i18n.t("Search symptoms or services", {ns: "web-scheduling"})}
          inputMode="text"
        />
        <Collapse in={showNoResults} unmountOnExit appear timeout={transitionSpeed}>
          <NoResults isNoSymptomsSearched={isNoSymptomsSearched} />
        </Collapse>

        <div>
          {isLoading ? (
            <ResultList.Loading
              keyPrefix="search-results"
              rowVariant={CareEntryRowVariant.MD}
              loadingRowCount={featuredCareSlugs.length}
              reasons={reasons}
            />
          ) : (
            resultsToRender.map(careEntry => (
              <ResultList.Item
                className=""
                hide={!topicExpansionStack.isEmpty}
                key={`search-results-${careEntry.name}`}
                transitionSpeed={transitionSpeed}
              >
                <Row
                  careEntry={careEntry}
                  selectedLocation={location}
                  query={query}
                  reasons={reasons}
                />
              </ResultList.Item>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(AppointmentReasonPage);
